import { useTranslation } from "react-i18next";
import { withForm } from "@/components/form/form";
import { Text } from "@/components/Text";
import { AUTH_MODES, METHODS, TIMEOUTS, TRANSPORTS, toValues } from "./connectorValues";
import { OAuthFields } from "./OAuthFields";

/** Type, URL, sign-in and (for an API endpoint) the one tool it becomes. */
export const EndpointFields = withForm({
  defaultValues: toValues(),
  props: { creating: true },
  render: function EndpointFieldsRender({ form, creating }) {
    const { t } = useTranslation("connectors");
    return (
      <>
        <form.Subscribe selector={(s) => s.values.transport}>
          {(transport) => (
            <div className="flex flex-col gap-1">
              {creating ? (
                <form.AppField name="transport">
                  {(field) => (
                    <field.SelectField
                      label={t("form.transport")}
                      options={TRANSPORTS.map((value) => ({ value, label: t(`form.transports.${value}`) }))}
                    />
                  )}
                </form.AppField>
              ) : (
                <Text variant="body-sm">
                  {t("form.transport")}: {t(`form.transports.${transport}`)}
                </Text>
              )}
              <Text variant="caption" tone="tertiary">
                {t(`form.transportHelp.${transport}`)}
              </Text>
            </div>
          )}
        </form.Subscribe>

        <form.Subscribe selector={(s) => s.values.transport}>
          {(transport) => (
            <div className="flex flex-col gap-1">
              <form.AppField name="url">
                {(field) => (
                  <field.TextField
                    label={t("form.url")}
                    required
                    placeholder={transport === "http" ? t("form.httpUrlPlaceholder") : t("form.urlPlaceholder")}
                  />
                )}
              </form.AppField>
              {transport === "http" && (
                <Text variant="caption" tone="tertiary">
                  {t("form.httpUrlHelp")}
                </Text>
              )}
            </div>
          )}
        </form.Subscribe>

        <form.AppField name="authMode">
          {(field) => (
            <field.SelectField
              label={t("form.authMode")}
              options={AUTH_MODES.map((value) => ({ value, label: t(`form.authModes.${value}`) }))}
            />
          )}
        </form.AppField>

        <form.Subscribe selector={(s) => [s.values.authMode, s.values.authHeader, s.values.authScheme] as const}>
          {([authMode, header, scheme]) =>
            authMode !== "none" &&
            authMode !== "oauth" && (
              <div className="flex flex-col gap-1 border-l-2 border-border-subtle pl-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <form.AppField name="authHeader">
                    {(field) => <field.TextField label={t("form.authHeader")} />}
                  </form.AppField>
                  <form.AppField name="authScheme">
                    {(field) => <field.TextField label={t("form.authScheme")} />}
                  </form.AppField>
                </div>
                <Text variant="caption" tone="tertiary">
                  {t("form.authSchemeHelp", { header: header || "Authorization", scheme })}
                </Text>
              </div>
            )
          }
        </form.Subscribe>

        <form.Subscribe selector={(s) => s.values.authMode}>
          {(authMode) => authMode === "oauth" && <OAuthFields form={form} />}
        </form.Subscribe>

        <form.AppField name="timeout">
          {(field) => (
            <field.SelectField
              label={t("form.timeout")}
              options={TIMEOUTS.map((value) => ({ value, label: t("form.timeoutValue", { seconds: value }) }))}
            />
          )}
        </form.AppField>

        <form.Subscribe selector={(s) => [s.values.transport, s.values.method] as const}>
          {([transport, method]) =>
            transport === "http" && (
              <div className="flex flex-col gap-5 border-l-2 border-border-subtle pl-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <form.AppField name="method">
                    {(field) => (
                      <field.SelectField
                        label={t("form.method")}
                        options={METHODS.map((value) => ({ value, label: value }))}
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="toolName">
                    {(field) => (
                      <field.TextField
                        label={t("form.toolName")}
                        required
                        placeholder={t("form.toolNamePlaceholder")}
                      />
                    )}
                  </form.AppField>
                </div>
                <form.AppField name="toolDescription">
                  {(field) => (
                    <field.TextareaField
                      label={t("form.toolDescription")}
                      required
                      rows={2}
                      placeholder={t("form.toolDescriptionPlaceholder")}
                    />
                  )}
                </form.AppField>
                <form.AppField name="parameters">
                  {(field) => <field.TextareaField label={t("form.parameters")} rows={6} />}
                </form.AppField>
                {method !== "GET" && method !== "DELETE" && (
                  <form.AppField name="bodyTemplate">
                    {(field) => <field.TextareaField label={t("form.bodyTemplate")} rows={3} />}
                  </form.AppField>
                )}
                <form.AppField name="responsePath">
                  {(field) => (
                    <field.TextField label={t("form.responsePath")} placeholder={t("form.responsePathPlaceholder")} />
                  )}
                </form.AppField>
              </div>
            )
          }
        </form.Subscribe>
      </>
    );
  },
});
